import {Injectable} from '@angular/core';
import {ChartExportLegendItem, ChartExportRequest} from '@/app/model/common';

@Injectable({providedIn: 'root'})
export class ChartExportService {

    async exportChart(request: ChartExportRequest): Promise<void> {
        const composedCanvas = this.buildExportCanvas(request);
        const fileName = request.fileName || 'chart';

        if (request.format === 'PDF') {
            const {jsPDF} = await import('jspdf');
            const pdf = new jsPDF('l', 'px', [composedCanvas.width, composedCanvas.height]);
            pdf.addImage(composedCanvas.toDataURL('image/png', 1.0), 'PNG', 15, 15, 0, 0);
            pdf.save(`${fileName}.pdf`);
            return;
        }

        if (request.format === 'SVG') {
            const svg = this.buildSvg(composedCanvas);
            this.downloadBlob(new Blob([svg], {type: 'image/svg+xml'}), `${fileName}.svg`);
            return;
        }

        const mimeType = request.format === 'JPEG' ? 'image/jpeg' : 'image/png';
        const extension = request.format === 'JPEG' ? 'jpeg' : 'png';
        const imageUrl = composedCanvas.toDataURL(mimeType, 1.0);
        this.downloadDataUrl(imageUrl, `${fileName}.${extension}`);
    }

    private buildExportCanvas(request: ChartExportRequest): HTMLCanvasElement {
        const {chartCanvas, metadataRows, bannerDetails, legendItems = []} = request;
        const bannerTitle = bannerDetails?.title || '';
        const bannerDescription = bannerDetails?.description || '';
        const hasBanner = Boolean(bannerTitle || bannerDescription);
        const hasLegends = legendItems.length > 0;
        const exportPadding = 16;
        const cardPadding = 24;
        const chartBottomPadding = 24;
        const metadataTop = cardPadding;
        const metadataRowHeight = 28;
        const contentX = exportPadding + cardPadding;
        const contentWidth = chartCanvas.width;
        const bannerSpacingTop = 12;
        const bannerSpacingBottom = hasBanner ? 16 : 0;
        const bannerInnerPaddingX = 16;
        const bannerInnerPaddingY = 16;
        const bannerTextWidth = contentWidth - (bannerInnerPaddingX * 2);
        const bannerTitleFont = '700 14px Arial';
        const bannerDescriptionFont = '400 13px Arial';
        const legendFont = '400 14px Arial';
        const legendSpacingBottom = hasLegends ? 16 : 0;
        const legendHeight = hasLegends
            ? this.calculateLegendHeight(legendItems, contentWidth, legendFont)
            : 0;
        const bannerTitleLines = bannerTitle
            ? this.wrapCanvasText(bannerTitle, bannerTextWidth, bannerTitleFont)
            : [];
        const bannerDescriptionLines = bannerDescription
            ? this.wrapCanvasText(bannerDescription, bannerTextWidth, bannerDescriptionFont)
            : [];
        const bannerHeight = hasBanner
            ? (bannerInnerPaddingY * 2)
                + (bannerTitleLines.length * 18)
                + (bannerDescriptionLines.length > 0 ? 8 : 0)
                + (bannerDescriptionLines.length * 16)
            : 0;
        const chartTop = metadataTop
            + (metadataRows.length * metadataRowHeight)
            + (hasBanner ? bannerSpacingTop + bannerHeight + bannerSpacingBottom : 0)
            + (hasLegends ? legendHeight + legendSpacingBottom : 0);

        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = chartCanvas.width + (exportPadding * 2) + (cardPadding * 2);
        finalCanvas.height = chartCanvas.height + chartTop + exportPadding + chartBottomPadding;

        const finalCtx = finalCanvas.getContext('2d');
        if (!finalCtx) {
            return finalCanvas;
        }

        finalCtx.fillStyle = '#F7F8FB';
        finalCtx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

        const cardX = exportPadding;
        const cardY = exportPadding;
        const cardWidth = finalCanvas.width - (exportPadding * 2);
        const cardHeight = finalCanvas.height - (exportPadding * 2);

        this.drawRoundedRect(finalCtx, cardX, cardY, cardWidth, cardHeight, 8);
        finalCtx.fillStyle = '#FFFFFF';
        finalCtx.fill();

        let textY = exportPadding + metadataTop;
        metadataRows.forEach((row) => {
            finalCtx.font = '700 14px Arial';
            finalCtx.fillStyle = '#313B49';
            const labelText = `${row.label}: `;
            finalCtx.fillText(labelText, contentX, textY);

            const labelWidth = finalCtx.measureText(labelText).width;
            finalCtx.font = '400 14px Arial';
            finalCtx.fillStyle = '#667790';
            finalCtx.fillText(row.value, contentX + labelWidth, textY);
            textY += metadataRowHeight;
        });

        let chartStartY = exportPadding + metadataTop + (metadataRows.length * metadataRowHeight);

        if (hasBanner) {
            chartStartY += bannerSpacingTop;

            this.drawRoundedRect(finalCtx, contentX, chartStartY, contentWidth, bannerHeight, 8);
            finalCtx.fillStyle = bannerDetails.backgroundColor || '#EFF2F7';
            finalCtx.fill();
            finalCtx.lineWidth = 2;
            finalCtx.strokeStyle = bannerDetails.borderColor || '#C2CDDD';
            finalCtx.stroke();

            finalCtx.textBaseline = 'top';
            finalCtx.fillStyle = '#000103';
            let bannerTextY = chartStartY + bannerInnerPaddingY;

            finalCtx.font = bannerTitleFont;
            bannerTitleLines.forEach((line) => {
                finalCtx.fillText(line, contentX + bannerInnerPaddingX, bannerTextY);
                bannerTextY += 18;
            });

            if (bannerDescriptionLines.length > 0) {
                bannerTextY += 8;
                finalCtx.font = bannerDescriptionFont;
                bannerDescriptionLines.forEach((line) => {
                    finalCtx.fillText(line, contentX + bannerInnerPaddingX, bannerTextY);
                    bannerTextY += 16;
                });
            }

            chartStartY += bannerHeight + bannerSpacingBottom;
        }

        if (hasLegends) {
            this.drawLegendChips(finalCtx, legendItems, contentX, chartStartY, contentWidth, legendFont);
            chartStartY += legendHeight + legendSpacingBottom;
        }

        finalCtx.drawImage(chartCanvas, contentX, chartStartY);
        return finalCanvas;
    }

    private buildSvg(canvas: HTMLCanvasElement): string {
        const imageUrl = canvas.toDataURL('image/png', 1.0);
        return `
<svg xmlns="http://www.w3.org/2000/svg" width="${canvas.width}" height="${canvas.height}" viewBox="0 0 ${canvas.width} ${canvas.height}">
  <image href="${imageUrl}" width="${canvas.width}" height="${canvas.height}" />
</svg>`.trim();
    }

    private downloadDataUrl(dataUrl: string, filename: string): void {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename;
        link.click();
    }

    private downloadBlob(blob: Blob, filename: string): void {
        const url = URL.createObjectURL(blob);
        this.downloadDataUrl(url, filename);
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }

    private drawLegendChips(
        context: CanvasRenderingContext2D,
        legendItems: ChartExportLegendItem[],
        startX: number,
        startY: number,
        maxWidth: number,
        font: string,
    ): void {
        const chipHeight = 32;
        const chipGap = 8;
        const chipPaddingX = 12;
        const dotRadius = 8;
        const dotGap = 10;
        const rowGap = 8;
        let currentX = startX;
        let currentY = startY;

        context.font = font;
        context.textBaseline = 'middle';

        legendItems.forEach((item) => {
            const textWidth = context.measureText(item.label).width;
            const chipWidth = chipPaddingX * 2 + (dotRadius * 2) + dotGap + textWidth;

            if ((currentX - startX) + chipWidth > maxWidth) {
                currentX = startX;
                currentY += chipHeight + rowGap;
            }

            this.drawRoundedRect(context, currentX, currentY, chipWidth, chipHeight, 12);
            context.fillStyle = item.dimmed ? '#EFF2F7' : '#EFF2F7';
            context.fill();
            context.lineWidth = 1;
            context.strokeStyle = '#C2CDDD';
            context.stroke();

            context.beginPath();
            context.fillStyle = item.color;
            context.globalAlpha = item.dimmed ? 0.45 : 1;
            context.arc(currentX + chipPaddingX + dotRadius, currentY + (chipHeight / 2), dotRadius / 1.4, 0, Math.PI * 2);
            context.fill();
            context.globalAlpha = 1;

            context.fillStyle = '#000103';
            context.globalAlpha = item.dimmed ? 0.55 : 1;
            context.fillText(
                item.label,
                currentX + chipPaddingX + (dotRadius * 2) + dotGap,
                currentY + (chipHeight / 2),
            );
            context.globalAlpha = 1;

            currentX += chipWidth + chipGap;
        });
    }

    private calculateLegendHeight(
        legendItems: ChartExportLegendItem[],
        maxWidth: number,
        font: string,
    ): number {
        const measurementCanvas = document.createElement('canvas');
        const context = measurementCanvas.getContext('2d');
        if (!context || legendItems.length === 0) {
            return 0;
        }

        const chipHeight = 32;
        const chipGap = 8;
        const chipPaddingX = 12;
        const dotRadius = 8;
        const dotGap = 10;
        const rowGap = 8;
        let rowCount = 1;
        let currentRowWidth = 0;

        context.font = font;

        legendItems.forEach((item) => {
            const textWidth = context.measureText(item.label).width;
            const chipWidth = chipPaddingX * 2 + (dotRadius * 2) + dotGap + textWidth;
            const nextWidth = currentRowWidth === 0 ? chipWidth : currentRowWidth + chipGap + chipWidth;

            if (nextWidth > maxWidth && currentRowWidth > 0) {
                rowCount += 1;
                currentRowWidth = chipWidth;
                return;
            }

            currentRowWidth = nextWidth;
        });

        return (rowCount * chipHeight) + ((rowCount - 1) * rowGap);
    }

    private drawRoundedRect(
        context: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        radius: number,
    ): void {
        context.beginPath();
        context.moveTo(x + radius, y);
        context.lineTo(x + width - radius, y);
        context.quadraticCurveTo(x + width, y, x + width, y + radius);
        context.lineTo(x + width, y + height - radius);
        context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        context.lineTo(x + radius, y + height);
        context.quadraticCurveTo(x, y + height, x, y + height - radius);
        context.lineTo(x, y + radius);
        context.quadraticCurveTo(x, y, x + radius, y);
        context.closePath();
    }

    private wrapCanvasText(text: string, maxWidth: number, font: string): string[] {
        const measurementCanvas = document.createElement('canvas');
        const measurementContext = measurementCanvas.getContext('2d');
        if (!measurementContext || !text) {
            return [];
        }

        measurementContext.font = font;
        const words = text.split(/\s+/);
        const lines: string[] = [];
        let currentLine = '';

        words.forEach((word) => {
            const nextLine = currentLine ? `${currentLine} ${word}` : word;
            if (measurementContext.measureText(nextLine).width <= maxWidth) {
                currentLine = nextLine;
                return;
            }

            if (currentLine) {
                lines.push(currentLine);
            }
            currentLine = word;
        });

        if (currentLine) {
            lines.push(currentLine);
        }

        return lines;
    }
}
